import { Flex, Heading, Text } from "@chakra-ui/react";
import { useEffect, useState } from "react";
import { usePix } from "@/hooks/usePix";
import { CopyIcon, DocumentIcon, EmailIcon, PhoneIcon, RandomIcon } from "@/styles/icons";
import { clearMask, copyTextToClipboard, handleErrors,showToast } from "@/utils";
import { FormModal, Input } from "@/components";
import { ListSelector } from "@/components/Forms/Selector";
import { useForm } from "react-hook-form";
import { PixKeyBankingTypes } from "@/models";
import isEmail from 'validator/lib/isEmail';
import PixKeysTable from "./pixkeys-table";
import { Button, PageHeading } from "barte-design-system";
import { useNavigate } from "react-router-dom";

const PixKeysComponent = () => {
    const { getPixKeys, createPixKey, deletePixKey } = usePix();
    const userDocument = localStorage.getItem("barte-user-document");
    const [debitPixKeys, setDebitPixKeys] = useState([]);
    const [investmentPixKeys, setInvestmentPixKeys] = useState([]);
    const [isNewPixKeyFormOpen, setIsNewPixKeyFormOpen] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isLoadingData, setIsLoadingData] = useState(false);
    const [createPixSection, setCreatePixSection] = useState("form");
    const navigate = useNavigate();
    const {
        register,
        formState: { errors },
        watch,
        getValues,
        setValue,
        setError,
        clearErrors
    } = useForm();
    async function getInitialData() {
        try {
            setIsLoadingData(true);
            const response = await getPixKeys();
            const allKeys = response?.data || [];
            const investmentKeys = allKeys.filter((item: any) => item?.accountType === "INVESTMENT_ACCOUNT");
            const debitKeys = allKeys.filter((item: any) => item?.accountType !== "INVESTMENT_ACCOUNT");
            setDebitPixKeys(debitKeys);
            setInvestmentPixKeys(investmentKeys);
        } catch (error) {
            handleErrors(error);
        } finally {
            setIsLoadingData(false);
        }
    }

    const deleteKey = async(id: string, keyType: string) => {
        try {
            await deletePixKey(id, keyType);
            showToast({
                type: "success",
                message: "Chave pix deletada com sucesso.",
            });
            getInitialData();
        } catch (error) {
            handleErrors(error)
        } 
    }
      
    const getKeyType = (type?: string) => {
        const KeyType = {
          email: "Email",
          phone: "Celular",
          evp: "Aleatória",
          cnpj: "CNPJ",
        };
        return KeyType[type] || type;
    };
    const getKeyTypeIcon = (type?: string) => {
        const KeyType = {
          email: <EmailIcon size={24} />,
          phone: <PhoneIcon size={24} />,
          evp: <RandomIcon size={24} />,
          cnpj: <DocumentIcon size={24} />,
        };
        return KeyType[type] || type;
    };
    const getKeyTypeMask = (type?: string) => {
        const KeyType = {
          email: "",
          phone: "(99) 99999-9999",
          evp: "",
          cnpj: "cnpj",
        };
        return KeyType[type] || "";
    };

    const getFormModalTitle = (type?: string) => {
        const FormModalTitle = {
          form: "Cadastrar nova Chave Pix",
          success: "Chave Pix criada",
          error: "Erro",
        };
        return FormModalTitle[type] || "Cadastrar nova Chave Pix";
    };

    const getFormModalButton = (type?: string) => {
        const FormModalTitle = {
          form: "Cadastrar",
          success: "Confirmar",
          error: "Confirmar",
        };
        return FormModalTitle[type] || "Cadastrar";
    };

    const submitForm = async() => {
        const values = getValues();
        if(values?.pixKeyType === "email" && !isEmail(values?.pixKey)) {
            setError("pixKey", { type: "custom", message: "Digite um e-mail inválido." })
            return
        }
        let formData: any = {
            keyType: values?.pixKeyType,
            key: values?.pixKeyType === "phone"
                ? `55${clearMask(values?.pixKey)}`
                : clearMask(values?.pixKey)
        };
        if (values?.pixKeyType === "email") {
            formData.key = values?.pixKey;
        }
        try {
            clearErrors();
            setIsSubmitting(true);
            const response = await createPixKey(formData);
            if(values?.pixKeyType === "evp") {
                setValue("pixKey", response?.data?.keyValue);
            }
            if(values?.pixKeyType === "phone") {
                setCreatePixSection("otp");
                return
            }
            setCreatePixSection("success");
            getInitialData();
        } catch(error) {
            error?.response?.data?.errors.map((error) => {
                showToast({
                  type: "error",
                  message: `${error?.code} - ${error?.description}`
                })
            });
            setCreatePixSection("error")
        } finally {
            // setIsNewPixKeyFormOpen(false);
            setIsSubmitting(false);
        }
    }
    const handleConfirmModal = (type?: string) => {
        const FormModalTitle = {
          form: () => submitForm(),
          otp: () => setCreatePixSection("success"),
          success: () => setIsNewPixKeyFormOpen(false),
          error: () => setIsNewPixKeyFormOpen(false),
        };
        return FormModalTitle[type];
    };
    useEffect(() => {
        getInitialData();
    }, [])

    useEffect(() => {
        setCreatePixSection("form")
        if(watch("pixKeyType") === "cnpj") {
            setValue("pixKey", `${userDocument}`);
        } else {
            setValue("pixKey", "");
        }
    }, [watch("pixKeyType"), isNewPixKeyFormOpen])
    return (
        <>
            <PageHeading
                title="Chaves Pix"
                primaryActionLabel="Nova Chave Pix"
                onPrimaryAction={() => setIsNewPixKeyFormOpen(true)}
            />
            <FormModal
                isOpen={isNewPixKeyFormOpen}
                onClose={() => setIsNewPixKeyFormOpen(false)}
                handleConfirmModal={handleConfirmModal(createPixSection)}
                title={getFormModalTitle(createPixSection)}
                isSubmitting={isSubmitting}
                confirmationButtonText={getFormModalButton(createPixSection)}
                disableWhenIsFormEmpty={watch("pixKeyType") !== "evp" && (watch("pixKey") === "" || watch("pixKey") === undefined)}
                variant="primaryPink"
            >
                {createPixSection === "form" && (
                    <Flex 
                        w="100%"
                        flexDir="column"
                        gap="1rem"
                    >
                        <ListSelector
                            label="Tipo de chave"
                            placeholder="Selecione"
                            options={PixKeyBankingTypes}
                            name="pixKeyType"
                            setDataUserSelectCard={(value) => setValue("pixKeyType", value)}
                            dataUserSelectCard={watch("pixKeyType")}
                            errors={errors?.pixKeyType}
                        />
                        {watch("pixKeyType") !== "evp" && watch("pixKeyType") !== "" && watch("pixKeyType") !== undefined && (
                            <Input
                                label={getKeyType(watch("pixKeyType"))}
                                type="text"
                                name="pixKey"
                                register={{ ...register("pixKey") }}
                                errors={errors?.pixKey}
                                isDisabled={watch("pixKeyType") === "cnpj"}
                                mask={getKeyTypeMask(watch("pixKeyType"))}
                            />
                        )}
                    </Flex>
                )}
                {createPixSection === "otp" && (
                    <Flex 
                        w="100%"
                        flexDir="column"
                        gap=".5rem"
                    >
                        <Input
                            label="Insira o código que enviamos para você por SMS"
                            type="text"
                            name="otpValue"
                            register={{ ...register("otpValue") }}
                        />
                        <Button
                            style={{ maxWidth: "165px" }}
                            variant="link"
                        >
                            Enviar código novamente
                        </Button>
                    </Flex>
                )}
                {createPixSection === "success" && (
                    <Flex
                        w="100%"
                        direction="column"
                        gap="1rem"
                    >
                        <Text>Sua Chave Pix foi criada com sucesso!</Text>
                        <Flex 
                            w="100%"
                            justifyContent="space-between"
                        >
                            <Flex
                                alignItems="center"
                                gap="1rem"
                            >
                                {getKeyTypeIcon(watch("pixKeyType"))}
                                <Flex
                                    flexDir="column"
                                >
                                    <Text
                                        fontSize=".8rem"
                                        color="newGray.400"
                                    >
                                        {getKeyType(watch("pixKeyType"))}
                                    </Text>
                                    <Text
                                        fontSize="1rem"
                                    >
                                        {watch("pixKey")}
                                    </Text>
                                </Flex>
                            </Flex>
                            <Button
                                variant="outline"
                                onClick={() => {
                                    copyTextToClipboard(watch("pixKey"));
                                }}
                            
                            >
                                <CopyIcon color="#6E7A91" />
                            </Button>
                        </Flex>
                    </Flex>
                )}      
                {createPixSection === "error" && (
                    <Flex
                        w="100%"
                        direction="column"
                        gap="1rem"
                    >
                        <Text>Houve um erro na criação da sua Chave Pix.</Text>
                        <Text>Por favor, tente novamente em alguns instantes.</Text> 
                    </Flex>
                )}    
            </FormModal>
        
            <Flex
                w="100%"
                flexDir="column"
                gap="1rem"
            >
                {(debitPixKeys && debitPixKeys.length > 0) && (
                    <>
                        <Heading fontSize="1rem">Conta corrente</Heading>
                        <PixKeysTable 
                            isLoading={isLoadingData}
                            data={debitPixKeys}
                            deleteKey={deleteKey}
                        />
                    </>
                )}
                {(investmentPixKeys && investmentPixKeys.length > 0) && (
                    <>
                        <Heading fontSize="1rem">Conta investimento</Heading>
                        <PixKeysTable 
                            isLoading={isLoadingData}
                            data={investmentPixKeys}
                            deleteKey={deleteKey}
                        />
                    </>
                )}
            </Flex>
        </>
    );
}

export { PixKeysComponent };
